import React, { useRef, useState, useMemo, useCallback } from "react";
import classNames from "classnames";
import {
  BasicIcon,
  Tooltip,
  ToolTipPosition,
  Collapsible,
} from "@pickit/pickit-components";
import { FileMessage, FileMessageType, FilesContainerProps } from "./types";
import i18n from "config/i18next";
import Confirm from "../confirm/Confirm";
import "./filesContainer.scss";
import "./i18n";
import { useTranslation } from "react-i18next";

const filesError = {
  FORMAT: i18n.t("filesContainer:formatError"),
  SIZE: i18n.t("filesContainer:sizeError"),
  LOAD: i18n.t("filesContainer:loadError"),
  NETWORK: i18n.t("filesContainer:serverError"),
};

const FilesContainer = <Tags,>({
  testId,
  id,
  files,
  names,
  showDeleteIcon,
  onOpenFile,
  onUploadFile,
  onReplaceFile,
  onDeleteFile,
}: FilesContainerProps<Tags>) => {
  const { t } = useTranslation();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [messages, showMessages] = useState<FileMessageType<Tags>>();
  const [fileTag, setFileTag] = useState<Tags | null>(null);
  const hasError = useMemo(
    () => !files.content.every((f) => !f.error || f.error === "NONE"),
    [files]
  );

  const setFileMessage = useCallback(
    (tag: string, type: FileMessage) => {
      showMessages(
        (prev) => ({ ...prev, [tag]: type } as FileMessageType<Tags>)
      );
    },
    [showMessages]
  );

  const handleOpenFile = (tag: Tags, isUpload: boolean) => {
    if (isUpload && onOpenFile) onOpenFile(tag);
  };

  const handleUploadFile = useCallback(
    (tag: Tags, isUpload: boolean) => {
      if (!isUpload && setFileTag) {
        if (inputFileRef.current) {
          setFileTag(tag);
          inputFileRef.current.click();
        }
      }
    },
    [setFileTag]
  );

  const handleReplaceFile = useCallback(
    (tag: Tags, isUpload: boolean) => {
      if (isUpload) {
        setFileMessage(tag as unknown as string, "REPLACE");
      }
    },
    [setFileMessage]
  );

  const handleDeleteFile = useCallback(
    (tag: Tags, isUpload: boolean) => {
      if (isUpload) {
        setFileMessage(tag as unknown as string, "DELETE");
      }
    },
    [setFileMessage]
  );

  const handleConfirmationCancel = useCallback(
    (tag: Tags) => {
      setFileMessage(tag as unknown as string, "NONE");
    },
    [setFileMessage]
  );

  const handleConfirmationAccept = useCallback(
    (tag: Tags) => {
      const tagStr = tag as unknown as string;
      if (messages && messages[tagStr]) {
        if (messages[tagStr] === "DELETE" && onDeleteFile) {
          onDeleteFile(tag);
          setFileMessage(tagStr, "NONE");
        }
        if (messages[tagStr] === "REPLACE") {
          if (inputFileRef.current) {
            setFileTag(tag);
            inputFileRef.current.click();
          }
        }
      }
    },
    [messages, onDeleteFile, setFileMessage, setFileTag]
  );

  const onFileChange = (event: React.FormEvent<HTMLInputElement>) => {
    const tag = fileTag as unknown as string;
    if (!fileTag || !inputFileRef.current) return;

    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    if (!file) return;

    inputFileRef.current.value = "";

    if (messages && messages[tag] === "REPLACE" && onReplaceFile) {
      onReplaceFile(fileTag, file);
      setFileMessage(tag, "NONE");
    } else if (onUploadFile) {
      onUploadFile(fileTag, file);
    }
  };

  const renderFolderIcon = useCallback(() => {
    const name = hasError
      ? "folder-error"
      : files.status === "COMPLETED"
      ? "folder-full"
      : "folder-add";
    return (
      <div className="folder-icon-container">
        <BasicIcon
          data-testId={testId + "-icon-" + name}
          id={id + "-icon-" + name}
          name={name}
          color={
            hasError
              ? "#FF1515"
              : files.status === "COMPLETED"
              ? "#4D4D4D"
              : "#0098FF"
          }
          size="medium"
        />
      </div>
    );
  }, [id, testId, hasError, files.status]);

  const renderFileIcons = useCallback(
    (tag: Tags, isUpload: boolean) => {
      if (isUpload)
        return (
          <>
            <Tooltip
              message={t("global:replace")}
              position={ToolTipPosition.bottom}
            >
              <BasicIcon
                data-testId={`${testId}-icon-${tag}-replace`}
                id={`${id}-icon-${tag}-replace`}
                name="switch-vertical"
                color="#808080"
                size="small"
                onClick={() => handleReplaceFile(tag, isUpload)}
              />
            </Tooltip>
            {showDeleteIcon && (
              <Tooltip
                message={t("global:delete")}
                position={ToolTipPosition.bottom}
              >
                <BasicIcon
                  data-testId={`${testId}-icon-${tag}-delete`}
                  id={`${id}-icon-${tag}-delete`}
                  name="trash"
                  color="#808080"
                  size="small"
                  onClick={() => handleDeleteFile(tag, isUpload)}
                />
              </Tooltip>
            )}
          </>
        );

      return (
        <Tooltip message={t("global:upload")} position={ToolTipPosition.bottom}>
          <BasicIcon
            data-testId={`${testId}-icon-${tag}-upload`}
            id={`${id}-icon-${tag}-upload`}
            name="upload"
            color="#808080"
            size="small"
            onClick={() => handleUploadFile(tag, isUpload)}
          />
        </Tooltip>
      );
    },
    [
      id,
      testId,
      handleDeleteFile,
      handleReplaceFile,
      handleUploadFile,
      showDeleteIcon,
      t,
    ]
  );

  const renderFeedback = useCallback(
    (tag: Tags) => {
      const tagStr = tag as unknown as string;

      if (messages && messages[tagStr] && messages[tagStr] !== "NONE") {
        const message =
          messages[tagStr] === "DELETE"
            ? "filesContainer:delete"
            : "filesContainer:replace";
        return (
          <Confirm
            testId={`${testId}-confirm-${tagStr}`}
            question={t(message)}
            optionYesClicked={() => handleConfirmationAccept(tag)}
            optionNoClicked={() => handleConfirmationCancel(tag)}
          />
        );
      }

      const file = files.content.find((f) => f.tag === tag);
      if (!file || !file.error || file.error === "NONE") return null;

      return (
        <p
          className="message-error"
          id={`${id}-error-${tagStr}`}
          data-testId={`${testId}-error-${tagStr}`}
        >
          {filesError[file.error]}
        </p>
      );
    },
    [
      id,
      testId,
      files,
      handleConfirmationAccept,
      handleConfirmationCancel,
      messages,
      t,
    ]
  );

  return (
    <div
      data-testId={testId}
      id={id}
      className={classNames("files-container", {
        "background-error": !open && hasError,
      })}
    >
      <Collapsible onClick={setOpen}>
        <div
          className="files-header"
          data-testId={`${id}-container`}
          id={`${id}-container`}
        >
          {renderFolderIcon()}
          <p
            className={classNames("files-header-title", {
              "error-text": hasError,
            })}
          >
            {t("filesContainer:files")}
          </p>
        </div>
        <div className="files-content-container">
          {files.content.map((file) => {
            const tag = file.tag as unknown as string;
            return (
              <div
                key={tag}
                id={`${id}-${tag}`}
                data-testId={`${testId}-${tag}`}
                className="files-content-container-row content-row"
              >
                <div className="files-content-container-col-4 file-item-col">
                  <p
                    id={`${id}-${tag}-name`}
                    data-testId={`${testId}-${tag}-name`}
                    className={classNames("file-item-name", {
                      uploaded: file.isUpload,
                      error: file.error && file.error !== "NONE",
                    })}
                    onClick={() => handleOpenFile(file.tag, file.isUpload)}
                  >
                    {t(names[tag])}
                  </p>
                  <div className="file-item-icons">
                    {renderFileIcons(file.tag, file.isUpload)}
                  </div>
                </div>
                <div className="files-content-container-col-8">
                  {renderFeedback(file.tag)}
                </div>
              </div>
            );
          })}
        </div>
      </Collapsible>
      <input
        ref={inputFileRef}
        data-testId={testId + "-input-file"}
        id={id + "-input-file"}
        className="display-none"
        type="file"
        accept=".png,.jpg,.pdf"
        onChange={onFileChange}
      />
    </div>
  );
};

export default FilesContainer;
